import React, { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Custom hook that calculates value when dependencies are changed and memoize the result, similar to React.useMemo,
 * but with the ability to manually set the value like in React.useState.
 *
 * The actual value will be the last one: either calculated as a result of dependencies change or manually set.
 *
 * @template S - The type of the state value.
 * @param {function(stored?: S): S} factory - A function that calculates the state value.
 * @param {React.DependencyList | undefined} deps - An array of dependencies to determine when the state should be recalculated.
 * @returns {[S, React.Dispatch<React.SetStateAction<S>>]} - A tuple containing the current state value and a function to update it.
 */
export default function useMemoState<S>(
    factory: (stored?: S) => S,
    deps: React.DependencyList | undefined,
): [S, React.Dispatch<React.SetStateAction<S>>] {
    const lastStateRef = useRef<S>();

    useMemo(() => {
        lastStateRef.current = factory(lastStateRef.current);
    }, deps || []);
    const [_, reset] = useState(0);

    const set = useCallback<React.Dispatch<React.SetStateAction<S>>>((value: S | ((prevState: S) => S)) => {
        const newState = value instanceof Function ? value(lastStateRef.current as S) : value;
        if (lastStateRef.current !== newState) {
            lastStateRef.current = newState;
            reset((v) => v + 1);
        }
    }, []);

    return [lastStateRef.current as S, set];
}

// #region Tests

if (import.meta.vitest) {
    const { describe, test, expect, vi } = import.meta.vitest;
    describe('useMemoState', async () => {
        const { renderHook, act } = await import('@testing-library/react');

        test('calls to generator function ones and returns the given value', () => {
            const factory = vi.fn();
            factory.mockReturnValue('initial value');

            const { result, rerender } = renderHook(() => useMemoState(factory, []));
            expect(result.current[0]).toBe('initial value');
            expect(factory).toBeCalledTimes(1);

            rerender();
            expect(result.current[0]).toBe('initial value');
            expect(factory).toBeCalledTimes(1);
        });

        test('refreshes value when dependencies are changed', () => {
            const factory = vi.fn();
            factory.mockReturnValue('initial value');

            const { result, rerender } = renderHook((deps: [string, string]) => useMemoState(factory, deps), {
                initialProps: ['dep:1:a', 'dep:2:a'],
            });
            expect(result.current[0]).toBe('initial value');
            expect(factory).toBeCalledTimes(1);

            factory.mockReturnValue('second value');
            rerender(['dep:1:b', 'dep:2:a']);
            expect(result.current[0]).toBe('second value');
            expect(factory).toBeCalledTimes(2);

            factory.mockReturnValue('third value');
            rerender(['dep:1:b', 'dep:2:a']);
            expect(result.current[0]).toBe('second value');
            expect(factory).toBeCalledTimes(2);

            factory.mockReturnValue('fourth value');
            rerender(['dep:1:b', 'dep:2:b']);
            expect(result.current[0]).toBe('fourth value');
            expect(factory).toBeCalledTimes(3);
        });

        test('passes old value to factory', () => {
            const factory = vi.fn();
            factory.mockReturnValue('initial value');

            const { result, rerender } = renderHook((deps: [string]) => useMemoState(factory, deps), {
                initialProps: ['dep:a'],
            });
            expect(result.current[0]).toBe('initial value');
            expect(factory).toBeCalledTimes(1);
            expect(factory).toBeCalledWith(undefined);

            factory.mockReturnValue('second value');
            rerender(['dep:b']);
            expect(result.current[0]).toBe('second value');
            expect(factory).toBeCalledTimes(2);
            expect(factory).toBeCalledWith('initial value');

            factory.mockReturnValue('third value');
            rerender(['dep:c']);
            expect(result.current[0]).toBe('third value');
            expect(factory).toBeCalledTimes(3);
            expect(factory).toBeCalledWith('second value');
        });

        test('changes value after set was called', async () => {
            const factory = vi.fn();
            factory.mockReturnValue('initial value');

            const { result, rerender } = renderHook(() => useMemoState(factory, []));
            expect(result.current[0]).toBe('initial value');
            expect(factory).toBeCalledTimes(1);
            expect(factory).toBeCalledWith(undefined);

            await act(() => {
                result.current[1]('second value');
            });
            expect(result.current[0]).toBe('second value');
            expect(factory).toBeCalledTimes(1);

            rerender();
            expect(result.current[0]).toBe('second value');
            expect(factory).toBeCalledTimes(1);

            await act(() => {
                result.current[1]('third value');
            });
            expect(result.current[0]).toBe('third value');
            expect(factory).toBeCalledTimes(1);
        });

        test('changes values that was manually set when dependencies are updated', async () => {
            const factory = vi.fn();
            factory.mockReturnValue('initial value');

            const { result, rerender } = renderHook((deps: [string]) => useMemoState(factory, deps), {
                initialProps: ['dep:a'],
            });

            await act(() => {
                result.current[1]('second value');
            });
            expect(result.current[0]).toBe('second value');

            factory.mockReturnValue('third value');
            rerender(['dep:b']);
            expect(result.current[0]).toBe('third value');
            expect(factory).toBeCalledTimes(2);
            expect(factory).toBeCalledWith('second value');
        });

        test('provides set function with factory as an argument that receives previous value as an argument', async () => {
            const factory = vi.fn();
            factory.mockReturnValue('initial value');

            const set = vi.fn();
            set.mockReturnValue('second value');

            const { result, rerender } = renderHook((deps: [string]) => useMemoState(factory, deps), {
                initialProps: ['dep:a'],
            });

            await act(() => {
                result.current[1](set);
            });
            expect(result.current[0]).toBe('second value');
            expect(set).toBeCalledTimes(1);
            expect(set).toBeCalledWith('initial value');
            expect(factory).toBeCalledTimes(1);

            set.mockReturnValue('third value');
            await act(() => {
                result.current[1](set);
            });
            expect(result.current[0]).toBe('third value');
            expect(set).toBeCalledTimes(2);
            expect(set).toBeCalledWith('second value');
            expect(factory).toBeCalledTimes(1);

            factory.mockReturnValue('fourth value');
            rerender(['dep:b']);
            expect(result.current[0]).toBe('fourth value');
            expect(set).toBeCalledTimes(2);
            expect(factory).toBeCalledTimes(2);

            set.mockReturnValue('fifth value');
            await act(() => {
                result.current[1](set);
            });
            expect(result.current[0]).toBe('fifth value');
            expect(set).toBeCalledTimes(3);
            expect(set).toBeCalledWith('fourth value');
            expect(factory).toBeCalledTimes(2);
        });
    });
}

// #endregion
